import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, HostListener, OnInit, OnChanges, OnDestroy, SimpleChanges, Injector, DoCheck, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, AbstractControl } from '@angular/forms';

export interface DropdownOption {
  label: string;
  value: any;
  /** Optional color code (e.g. for patient comment categories). */
  commentColorCode?: number;
}

export interface DropdownConfig {
  label: string;
  options: DropdownOption[];
  value?: string | number | (string | number)[];
  placeholder?: string;
  id: string;
  name: string;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
}

/** Stack above app-modal main layer (100010); below nested child modals (100030+). */
export const DROPDOWN_MODAL_OVERLAY_Z_INDEX = 100020;

@Component({
  selector: 'app-dropdown',
  standalone: false,
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true
    }
  ]
})
export class DropdownComponent implements ControlValueAccessor, OnChanges, DoCheck, AfterViewChecked, OnDestroy {
  @Input() config!: DropdownConfig;
  @Input() label?: string;
  @Input() options: DropdownOption[] = [];
  @Input() value: string | number | (string | number)[] = '';
  @Input() placeholder: string = 'Select an option';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() multiple: boolean = false;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  /** When true, dropdown opens above the trigger (e.g. for fields near bottom of viewport). */
  @Input() preferOpenAbove: boolean = false;
  /** z-index for portaled menu; must exceed modal backdrop (100010). */
  @Input() overlayZIndex = DROPDOWN_MODAL_OVERLAY_Z_INDEX;

  @Output() valueChange = new EventEmitter<string | number | (string | number)[]>();
  @Output() blur = new EventEmitter<FocusEvent>();

  filteredOptions: DropdownOption[] = [];
  isOpen: boolean = false;
  selectedValues: any[] = [];
  openAbove: boolean = false;
  dropdownStyle: any = {};
  isTouched: boolean = false;
  hasScroll: boolean = false;
  private control: AbstractControl | null = null;
  @ViewChild('dropdownContainer', { static: false }) dropdownContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownMenu', { static: false }) dropdownMenu?: ElementRef<HTMLDivElement>;
  private readonly onScrollReposition = (): void => {
    if (this.isOpen) {
      this.calculateDropdownPosition();
    }
  };

  constructor(
    private elementRef: ElementRef,
    private injector: Injector,
    private cdr: ChangeDetectorRef
  ) {
    // Try to get the form control from the injector
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl) {
        this.control = ngControl.control;
      }
    } catch (e) {
      // NgControl not available, which is fine
    }
  }

  get hasValue(): boolean {
    if (this.isMultiple) {
      return Array.isArray(this.selectedValues) && this.selectedValues.length > 0;
    }
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  private onChange = (value: any) => { };
  private onTouched = () => { };

  ngOnInit(): void {
    this.filteredOptions = this.displayOptions;
    if (this.isMultiple && Array.isArray(this.value)) {
      this.selectedValues = [...this.value];
    } else if (!this.isMultiple && this.value) {
      // Initialize single select - no need for selectedValues array
    }
  }

  ngDoCheck(): void {
    // Sync component's touched state with form control's touched state
    if (this.control && this.control.touched && !this.isTouched) {
      this.isTouched = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      // Update the selected value when value input changes
      if (this.isMultiple && Array.isArray(this.value)) {
        this.selectedValues = [...this.value];
      }
      // For single select, the value is already set via @Input binding
      // The getDisplayText() method will find the matching option
    }
    if (changes['options']) {
      this.filteredOptions = this.displayOptions;
    }
  }

  onSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    let value: string | number | (string | number)[];

    if (this.isMultiple) {
      if (Array.isArray(event)) {
        value = event;
      } else {
        const target = event.target as HTMLSelectElement;
        const selectedOptions = Array.from(target.selectedOptions);
        value = selectedOptions.map(option => {
          const optionValue = option.value;
          return this.isNumeric(optionValue) ? parseFloat(optionValue) : optionValue;
        });
      }
    } else {
      const target = event.target as HTMLSelectElement;
      value = target.value;
      if (this.isNumeric(value)) {
        value = parseFloat(value);
      }
    }

    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  selectOption(option: DropdownOption, event: Event): void {
    event.stopPropagation();

    if (this.isMultiple) {
      this.toggleSelection(option, event);
    } else {
      // Single select: close dropdown and set value
      this.value = option.value;
      this.onChange(option.value);
      this.valueChange.emit(option.value);
      this.closeDropdown();
      this.onTouched();
    }
  }

  onSelectBlur(event: FocusEvent): void {
    this.isTouched = true;
    this.onTouched();
    this.blur.emit(event);
    // Sync touched state with control
    if (this.control) {
      this.control.markAsTouched();
    }
  }

  toggleDropdown(): void {
    if (!this.isDisabled) {
      if (!this.isOpen) {
        this.isOpen = true;
        this.cdr.detectChanges();
        this.attachDropdownMenuToBody();
        this.calculateDropdownPosition();
        setTimeout(() => this.checkScrollability(), 0);
      } else {
        this.closeDropdown();
      }
    }
  }

  ngOnDestroy(): void {
    this.closeDropdown();
  }

  private closeDropdown(): void {
    if (!this.isOpen && !this.dropdownMenu?.nativeElement) {
      return;
    }
    this.isOpen = false;
    document.removeEventListener('scroll', this.onScrollReposition, true);
    this.detachDropdownMenuFromBody();
  }

  private attachDropdownMenuToBody(): void {
    document.removeEventListener('scroll', this.onScrollReposition, true);
    document.addEventListener('scroll', this.onScrollReposition, true);
    const el = this.dropdownMenu?.nativeElement;
    if (!el || el.parentElement === document.body) {
      return;
    }
    document.body.appendChild(el);
  }

  private detachDropdownMenuFromBody(): void {
    document.removeEventListener('scroll', this.onScrollReposition, true);
    const el = this.dropdownMenu?.nativeElement;
    if (!el || el.parentElement !== document.body) {
      return;
    }
    try {
      el.remove();
    } catch {
      /* ignore */
    }
  }

  ngAfterViewChecked(): void {
    if (this.isOpen) {
      this.checkScrollability();
    }
  }

  checkScrollability(): void {
    if (this.dropdownContainer?.nativeElement) {
      const container = this.dropdownContainer.nativeElement;
      // Check if the dropdown menu itself is scrollable
      this.hasScroll = container.scrollHeight > container.clientHeight;
      this.cdr.detectChanges();
    }
  }

  private calculateDropdownPosition(): void {
    const triggerElement = this.elementRef.nativeElement.querySelector('.dropdown-trigger');
    if (!triggerElement) return;

    const rect = triggerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedDropdownHeight = 200;
    const gap = 6;
    const viewportPadding = 8;

    // Open above when preferred, or when there's not enough space below but enough above
    this.openAbove = this.preferOpenAbove || (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow);

    // Calculate dropdown width based on largest option content, clamped to viewport
    let dropdownWidth = rect.width;
    if (this.displayOptions && this.displayOptions.length > 0) {
      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.whiteSpace = 'nowrap';
      tempElement.style.fontSize = '14px';
      tempElement.style.padding = '0 16px';
      tempElement.style.fontFamily = window.getComputedStyle(document.body).fontFamily;
      document.body.appendChild(tempElement);

      let maxContentWidth = 0;
      this.displayOptions.forEach(option => {
        tempElement.textContent = option.label;
        const optionWidth = tempElement.offsetWidth;
        if (optionWidth > maxContentWidth) {
          maxContentWidth = optionWidth;
        }
      });

      document.body.removeChild(tempElement);
      if (maxContentWidth > 0) {
        dropdownWidth = Math.max(maxContentWidth, rect.width);
      }
    }

    // Clamp width so dropdown doesn't exceed viewport
    dropdownWidth = Math.min(dropdownWidth, viewportWidth - viewportPadding * 2);
    // Clamp left so dropdown stays within viewport (avoid cut-off on right or left)
    let left = rect.left;
    left = Math.max(viewportPadding, Math.min(left, viewportWidth - dropdownWidth - viewportPadding));

    // Max height so panel scrolls inside instead of being clipped by modal/viewport
    const maxHeightBelow = Math.max(150, Math.min(320, spaceBelow - gap - viewportPadding));
    const maxHeightAbove = Math.max(150, Math.min(320, spaceAbove - gap - viewportPadding));

    if (this.openAbove) {
      this.dropdownStyle = {
        position: 'fixed',
        bottom: `${viewportHeight - rect.top + gap}px`,
        left: `${left}px`,
        width: `${dropdownWidth}px`,
        'min-width': `${Math.min(rect.width, dropdownWidth)}px`,
        'max-height': `${maxHeightAbove}px`,
        'overflow-y': 'auto',
        'z-index': String(this.overlayZIndex)
      };
    } else {
      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${left}px`,
        width: `${dropdownWidth}px`,
        'min-width': `${Math.min(rect.width, dropdownWidth)}px`,
        'max-height': `${maxHeightBelow}px`,
        'overflow-y': 'auto',
        'z-index': String(this.overlayZIndex)
      };
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as Node;
    if (this.elementRef.nativeElement.contains(target)) {
      return;
    }
    const menu = this.dropdownMenu?.nativeElement;
    if (menu?.contains(target)) {
      return;
    }
    if (this.isOpen) {
      this.closeDropdown();
    }
  }

  // Only react to window resize; do NOT reposition on scroll so the open panel
  // stays visually fixed while the underlying content scrolls.
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      this.calculateDropdownPosition();
    }
  }

  isSelected(option: DropdownOption): boolean {
    if (this.isMultiple) {
      return this.selectedValues.includes(option.value);
    }
    // Compare values with type coercion for numbers
    const optionValue = option.value;
    const currentValue = this.value;
    if (typeof optionValue === 'number' && typeof currentValue === 'string') {
      return optionValue === parseFloat(currentValue);
    }
    if (typeof optionValue === 'string' && typeof currentValue === 'number') {
      return parseFloat(optionValue) === currentValue;
    }
    return this.value === option.value;
  }

  toggleSelection(option: DropdownOption, event: Event): void {
    event.stopPropagation();

    if (option.value === 'all') {
      if (this.isSelected(option)) {
        this.selectedValues = [];
      } else {
        this.selectedValues = this.displayOptions.map(opt => opt.value);
      }
    } else {
      // Remove 'all' if it was selected
      this.selectedValues = this.selectedValues.filter(v => v !== 'all');

      const index = this.selectedValues.indexOf(option.value);
      if (index > -1) {
        this.selectedValues.splice(index, 1);
      } else {
        this.selectedValues.push(option.value);
      }

      // If all other options are selected, add 'all'
      const nonAllOptions = this.displayOptions.filter(opt => opt.value !== 'all');
      if (nonAllOptions.length > 0 && nonAllOptions.every(opt => this.isSelected(opt))) {
        if (!this.selectedValues.includes('all') && this.displayOptions.some(opt => opt.value === 'all')) {
          this.selectedValues.push('all');
        }
      }
    }

    this.value = this.selectedValues;
    this.onChange(this.selectedValues);
    this.valueChange.emit(this.selectedValues);
  }

  getDisplayText(): string {
    if (this.isMultiple) {
      if (this.selectedValues.length === 0) {
        return this.displayPlaceholder;
      }

      // If 'all' is explicitly selected OR if all non-all options are selected
      const nonAllOptions = this.displayOptions.filter(opt => opt.value !== 'all');
      const allSelected = nonAllOptions.length > 0 && nonAllOptions.every(opt => this.isSelected(opt));

      if (this.selectedValues.includes('all') || allSelected) {
        const allOption = this.displayOptions.find(opt => opt.value === 'all');
        return allOption ? allOption.label : 'All Selected';
      }

      // Return all selected labels joined by commas
      const labels = this.selectedValues
        .map(val => this.displayOptions.find(opt => opt.value === val)?.label)
        .filter(l => !!l);

      return labels.join(', ');
    } else {
      // Single select - treat 0 as valid value (do not use !this.value)
      if (this.value === null || this.value === undefined || this.value === '') {
        return this.displayPlaceholder;
      }

      // Find option with type coercion for numbers
      const selectedOption = this.displayOptions.find(opt => {
        const optionValue = opt.value;
        const currentValue = this.value;
        if (typeof optionValue === 'number' && typeof currentValue === 'string') {
          return optionValue === parseFloat(currentValue);
        }
        if (typeof optionValue === 'string' && typeof currentValue === 'number') {
          return parseFloat(optionValue) === currentValue;
        }
        return opt.value === this.value;
      });
      return selectedOption ? selectedOption.label : this.displayPlaceholder;
    }
  }

  get displayText(): string {
    return this.getDisplayText();
  }


  private isNumeric(value: string): boolean {
    return !isNaN(parseFloat(value)) && isFinite(parseFloat(value));
  }

  // ControlValueAccessor implementation (0 is valid for status Open - don't use || which treats 0 as falsy)
  writeValue(value: string | number | (string | number)[]): void {
    this.value = (value != null && value !== '') ? value : (this.isMultiple ? [] : '');
    if (this.isMultiple && Array.isArray(this.value)) {
      this.selectedValues = [...this.value];
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get displayLabel(): string {
    return this.config?.label || this.label || '';
  }

  get displayId(): string {
    return this.config?.id || this.id || '';
  }

  get displayName(): string {
    return this.config?.name || this.name || '';
  }

  get displayOptions(): DropdownOption[] {
    return this.config?.options || this.options || [];
  }

  get displayPlaceholder(): string {
    return this.config?.placeholder || this.placeholder || 'Select an option';
  }

  getDisplayPlaceholder(): string {
    return this.displayPlaceholder;
  }

  get isMultiple(): boolean {
    return this.config?.multiple ?? this.multiple;
  }

  get isRequired(): boolean {
    return this.config?.required ?? this.required;
  }

  // hasError is kept for visual styling (red border) only
  // Error messages are handled by app-form-error-message component
  get hasError(): boolean {
    return !!(this.control && this.control.invalid && (this.control.touched || this.isTouched));
  }

  get isDisabled(): boolean {
    return this.config?.disabled ?? this.disabled;
  }

  getSelectStyle(): string {
    if (this.isMultiple) {
      return '';
    }
    return 'background-image: url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%234b5563\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E"); background-position: right 0.4rem center; background-size: 1.2em 1.2em;';
  }

  /**
   * Optional helper: convert commentColorCode (24‑bit RGB integer) to CSS rgb().
   * Safe for all dropdowns – returns null when option has no commentColorCode.
   */
  getCommentBackground(option: DropdownOption): string | null {
    const code = option?.commentColorCode;
    if (code === null || code === undefined) return null;
    const r = (code >> 16) & 0xff;
    const g = (code >> 8) & 0xff;
    const b = code & 0xff;
    return `rgb(${r},${g},${b})`;
  }

  /** Background color for the currently selected option (single-select only). Used for trigger swatch. */
  get selectedCommentBackground(): string | null {
    if (this.isMultiple) return null;
    const selected = this.displayOptions.find(opt => this.isSelected(opt));
    return selected ? this.getCommentBackground(selected) : null;
  }
}
